import { DashboardCard } from "@dashboard/components/Card";
import Money from "@dashboard/components/Money";
import { TableCell, TableRow } from "@material-ui/core";
import { ResponsiveTable } from "@saleor/macaw-ui";
import { Text } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl } from "react-intl";

import { paymentRecoveryMessages } from "../../messages";
import { PaymentRecoveryCheckoutDetails } from "../../utils";

type CheckoutLine = NonNullable<PaymentRecoveryCheckoutDetails["lines"]>[number];

interface PaymentRecoveryLineItemsProps {
  lines: CheckoutLine[];
}

/**
 * A lighter-weight stand-in for the real order product table
 * (src/orders/components/OrderDetailsDatagrid, a glide-data-grid engine
 * bound to OrderLineFragment) -- checkout lines lack an equivalent
 * fragment/column-adapter to plug into that engine, and a stranded
 * checkout's line count is small, so a plain ResponsiveTable (the same
 * table primitive TransactionEvents already uses below) matches the visual
 * column layout (product, quantity, price, total) without forking the
 * datagrid's column/cell-content machinery.
 */
export const PaymentRecoveryLineItems = ({ lines }: PaymentRecoveryLineItemsProps) => {
  const intl = useIntl();

  return (
    <DashboardCard>
      <DashboardCard.Header>
        <DashboardCard.Title>
          {intl.formatMessage(paymentRecoveryMessages.contentsSection)}
        </DashboardCard.Title>
      </DashboardCard.Header>
      <DashboardCard.Content paddingX={0}>
        {!lines.length ? (
          <Text color="default2" paddingX={6}>
            {intl.formatMessage(paymentRecoveryMessages.noLines)}
          </Text>
        ) : (
          <ResponsiveTable>
            <TableRow>
              <TableCell>{intl.formatMessage(paymentRecoveryMessages.colProduct)}</TableCell>
              <TableCell align="right">
                {intl.formatMessage(paymentRecoveryMessages.colQuantity)}
              </TableCell>
              <TableCell align="right">
                {intl.formatMessage(paymentRecoveryMessages.colPrice)}
              </TableCell>
              <TableCell align="right">
                {intl.formatMessage(paymentRecoveryMessages.colTotal)}
              </TableCell>
            </TableRow>
            {lines.map(line => (
              <TableRow key={line.id}>
                <TableCell>
                  <Text fontWeight="medium">{line.variant.product.name}</Text>
                  {line.variant.name && (
                    <Text size={2} color="default2">
                      {line.variant.name}
                    </Text>
                  )}
                </TableCell>
                <TableCell align="right">{line.quantity}</TableCell>
                <TableCell align="right">
                  <Money money={line.unitPrice.gross} />
                </TableCell>
                <TableCell align="right">
                  <Money money={line.totalPrice.gross} />
                </TableCell>
              </TableRow>
            ))}
          </ResponsiveTable>
        )}
      </DashboardCard.Content>
    </DashboardCard>
  );
};

PaymentRecoveryLineItems.displayName = "PaymentRecoveryLineItems";
export default PaymentRecoveryLineItems;
